import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import "../Styles/Calculator.css";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb";
import { ChevronDown, Lightbulb, SlashIcon, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import LoadingButton from "./LoadingButton";
import { UnitSystemContext } from "../context/UnitSystemContext";
import { useContext } from "react";
import { useProjectId } from "../context/ProjectIdContext";
import { useAuth } from "../context/AuthContext";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";
import { useProjectInfo } from "../context/ProjectInfoContext";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL

const Calculator = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const { projectData } = useProjectInfo();

  const { userSession } = useAuth(); // Get session from context

  const userType = userSession?.userType;

  const location = useLocation();
  const { projectId } = useProjectId();

  // const projectId = location.state?.projectId;
  const requestData = location.state?.requestData || "";
  // const fetchedProjectId = location.state?.fetchedProjectId;
  const pressureInputted = location.state?.workingPressure || 100;
  const soilDensityInputted = location.state?.soilDensity || 120;
  const modulusInputted = location.state?.modulusOfSoilRtn || 1000;
  const resinInputted = location.state?.resin || "";

  const [formData, setFormData] = useState({
    ...(userType === "applicator" ? { projectId } : {}), // Include projectId only for applicator
    performRecalculation: false,
    ASTMVersion: "",
    internalDiameter: "",
    lengthLined: "",
    holeDiameter: "",
    depthToInvert: "",
    groundwaterDepth: "",
    liveLoad: 0.0,
    vacuumCondition: "",
    resin: resinInputted,
    soilDensity: soilDensityInputted,
    modulusOfSoilRtn: modulusInputted,
    workingPressure: pressureInputted,
  });

  const navigate = useNavigate();
  const { unitSystem, setUnitSystem } = useContext(UnitSystemContext);
  const [currentPage, setCurrentPage] = useState("Calculator");
  const [showAlert, setShowAlert] = useState(false);
  // const [showOAlert, setShowOAlert] = useState(false);
  const [showHAlert, setShowHAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isInputValid, setIsInputValid] = useState(true);
  const [toastList, setToastList] = useState([]);
  const [isFormComplete, setIsFormComplete] = useState(false);

  const [projectDetails, setProjectDetails] = useState({
    title: "",
    location: "",
    clientName: "",
    sippInstaller: "",
    sippCalcDesignDate: "",
  });

  // const isFormComplete = Object.values(formData).every(
  //   (val) => val !== "" && val !== null && val !== undefined
  // );

  const breadcrumbs = [
    {
      label: "Project Information",
      link: "/projectinfo",
      isCurrent: currentPage === "ProjectInfo",
    },
    {
      label: "Calculator",
      link: "/calculator",
      isCurrent: currentPage === "Calculator",
    },
    {
      label: "Design Summary",
      link: "/designsummary",
      isCurrent: currentPage === "Design Summary",
    },
  ];

  // const handleSub = (e) => {
  //   e.preventDefault();
  //   setCurrentPage("Calculator");
  //   navigate("/calculator");
  // };

  useEffect(() => {
    // Clear localStorage formData on mount
    localStorage.removeItem("formData");

    // Insert stored parameters into formData
    setFormData((prev) => ({
      ...prev,
      workingPressure: projectData.workingPressure,
      soilDensity: projectData.soilDensity,
      modulusOfSoilRtn: projectData.modulusOfSoilRtn,
      resin: projectData.resin,
    }));
  }, [projectData]);

  // Track form changes
  useEffect(() => {
    const allFieldsFilled = Object.entries(formData).every(
      ([key, val]) =>
        key === "ovality" || (val !== "" && val !== null && val !== undefined),
    );
    setIsFormComplete(allFieldsFilled);
  }, [formData]);

  // Load resin from projectData when available
  useEffect(() => {
    if (projectData.resin) {
      setFormData((prev) => ({ ...prev, resin: projectData.resin }));
    }
  }, [projectData.resin]);

  // // Save formData to localStorage whenever it changes
  // useEffect(() => {
  //   localStorage.setItem("formData", JSON.stringify(formData));
  // }, [formData]);

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      if (formData[key] === "" || formData[key] === null) {
        if (key !== "ovality") {
          // Exclude ovality from validation
          newErrors[key] = `${key} is required`;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // const validateForm = () => {
  //   const newErrors = {};
  //   Object.keys(formData).forEach((key) => {
  //     if (formData[key] === "" || formData[key] === null) {
  //       newErrors[key] = `${key} is required`;
  //     }
  //   });
  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };

  // // Ensure projectId is updated if it exists in location.state
  // useEffect(() => {
  //   if (projectId) {
  //     setFormData((prev) => ({
  //       ...prev,
  //       projectId: projectId,
  //     }));
  //   }
  // }, [projectId]);

  // Sync formData.projectId with context projectId, ensuring it's only set for applicator
  useEffect(() => {
    if (userType === "applicator") {
      setFormData((prev) => ({
        ...prev,
        projectId,
      }));
    } else {
      setFormData((prev) => {
        const { projectId, ...rest } = prev;
        return rest; // Remove projectId if userType is not applicator
      });
    }
  }, [projectId, userType]);

  // Fetching Project details
  useEffect(() => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const credentials = `${username}:${password}`;
    const encodedCredentials = btoa(credentials); // Base64 encode the credentials

    if (userType === "applicator") {
      const fetchProjectDetails = async () => {
        // const projectId = location.state?.projectId;

        if (!projectId) {
          console.error("No project ID provided.");
          return;
        }

        try {
          const response = await fetch(`${API_URL}/api/project/${projectId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
            },
          });

          if (response.ok) {
            const result = await response.json();
            const project = result.data.data.project;

            // console.log("Project details fetched:", project);

            // Update state with fetched project details
            setProjectDetails({
              title: project.title,
              location: project.location,
              clientName: project.clientName,
              sippInstaller: project.sippInstaller,
              sippCalcDesignDate: project.sippCalcDesignDate,
            });
          } else {
            console.error(
              "Failed to fetch project details:",
              response.statusText,
            );
          }
        } catch (error) {
          console.error("Error fetching project details:", error);
        }
      };

      fetchProjectDetails();
    }
  }, [projectId, userType]); // Only runs when the `projectId` changes

  useEffect(() => {
    // Load saved data from localStorage
    const savedFormData = localStorage.getItem("formData");
    if (savedFormData) {
      setFormData(JSON.parse(savedFormData));
    } else {
      const savedProjectInfo = localStorage.getItem("projectInfo");
      if (savedProjectInfo) {
        const { workingPressure, soilDensity, modulusOfSoilRtn, resin } =
          JSON.parse(savedProjectInfo);
        setFormData((prev) => ({
          ...prev,
          ...(userType === "applicator" && { projectId }),
          workingPressure: workingPressure || 100,
          soilDensity: soilDensity || 120,
          modulusOfSoilRtn: modulusOfSoilRtn || 1000,
          resin: resin || "",
        }));
      }
    }
  }, [projectId, userType]);

  // Set System working pressure in FormData (when passed via location.state)
  useEffect(() => {
    if (
      pressureInputted ||
      soilDensityInputted ||
      modulusInputted ||
      resinInputted
    ) {
      setFormData((prev) => ({
        ...prev,
        ...(userType === "applicator" && { projectId }),
        workingPressure: pressureInputted || prev.workingPressure,
        soilDensity: soilDensityInputted || prev.soilDensity,
        modulusOfSoilRtn: modulusInputted || prev.modulusOfSoilRtn,
        resin: resinInputted || prev.resin,
      }));
    }
  }, [
    pressureInputted,
    soilDensityInputted,
    modulusInputted,
    resinInputted,
    projectId,
    userType,
  ]);

  // Populate formData with requestData or localStorage
  useEffect(() => {
    if (requestData && Object.keys(requestData).length > 0) {
      // Populate formData with requestData if it exists
      setFormData((prev) => ({
        ...prev,
        ...requestData,
      }));
    }
  }, [requestData]);

  useEffect(() => {
    if (requestData && Object.keys(requestData).length > 0) {
      // Ensure `formData` is only saved to localStorage when not populated by `requestData`
      return; // Exit if `requestData` is in use
    }

    // Save formData to localStorage if no requestData is being applied
    localStorage.setItem("formData", JSON.stringify(formData));
  }, [formData, requestData]);

  // console.log("Form values BEFORE conversion:", formData);

  // useEffect(() => {
  //   const savedUnit = localStorage.getItem("savedUnitSystem");
  //   if (savedUnit) {
  //     setUnitSystem(savedUnit);
  //   }
  // }, [setUnitSystem]);

  const handleSliderChange = async (checked) => {
    const newUnit = checked ? "si" : "imperial";

    //  STOP double conversion
    if (unitSystem === newUnit) return;

    // const previousUnit = unitSystem;

    const conversionType = checked ? "IMPERIAL_TO_SI" : "SI_TO_IMPERIAL";

    setUnitSystem(newUnit);
    localStorage.setItem("savedUnitSystem", newUnit);

    // const conversionType =
    //   previousUnit === "imperial" ? "IMPERIAL_TO_SI" : "SI_TO_IMPERIAL";

    const requestBody = {
      projectId,
      ASTMVersion: formData.ASTMVersion,
      internalDiameter: Number(formData.internalDiameter) || 0,
      lengthLined: Number(formData.lengthLined) || 0,
      holeDiameter: Number(formData.holeDiameter) || 0,
      depthToInvert: Number(formData.depthToInvert) || 0,
      groundwaterDepth: Number(formData.groundwaterDepth) || 0,
      liveLoad: Number(formData.liveLoad) || 0,
      vacuumCondition: formData.vacuumCondition,
      resin: formData.resin,
      soilDensity: Number(formData.soilDensity) || 0,
      modulusOfSoilRtn: Number(formData.modulusOfSoilRtn) || 0,
      workingPressure: Number(formData.workingPressure) || 0,
    };

    try {
      const token = localStorage.getItem("x-auth-token");

      const response = await fetch(
        `${API_URL}/api/calculate/convert-request-data`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`,
            "x-auth-token": token,
            "convertion-type": conversionType,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) throw new Error("Conversion failed");

      const result = await response.json();
      const converted = result.data.requestData;

      const round = (num, decimals = 2) =>
        Number(Number(num).toFixed(decimals));

      setFormData((prev) => {
        const roundedConverted = Object.fromEntries(
          Object.entries(converted).map(([key, value]) => [
            key,
            typeof value === "number" ? round(value) : value,
          ]),
        );

        return {
          ...prev,
          ...roundedConverted,
        };
      });
    } catch (error) {
      console.error("Unit conversion error:", error);
    }
  };

  // const handleSliderChange = async (checked) => {
  //   const selectedUnitSystem = checked ? "si" : "imperial";
  //   setUnitSystem(selectedUnitSystem);
  //   updateUnits(selectedUnitSystem);
  //   // setFormData({ ...formData });

  //   const conversionType = checked ? "IMPERIAL_TO_SI" : "SI_TO_IMPERIAL";

  //   const requestBody = {
  //     projectId: projectId,
  //     ASTMVersion: formData.ASTMVersion,
  //     internalDiameter: parseFloat(formData.internalDiameter) || 0,
  //     lengthLined: parseFloat(formData.lengthLined) || 0,
  //     holeDiameter: parseFloat(formData.holeDiameter) || 0,
  //     depthToInvert: parseFloat(formData.depthToInvert) || 0,
  //     groundwaterDepth: parseFloat(formData.groundwaterDepth) || 0,
  //     liveLoad: parseFloat(formData.liveLoad) || 0,
  //     vacuumCondition: formData.vacuumCondition,
  //     resin: formData.resin,
  //     soilDensity: parseFloat(formData.soilDensity) || 0,
  //     modulusOfSoilRtn: parseFloat(formData.modulusOfSoilRtn) || 0,
  //     workingPressure: parseFloat(formData.workingPressure) || 0,
  //   };

  //   // console.log(requestBody);

  //   try {
  //     const token = localStorage.getItem("x-auth-token");
  //     const response = await fetch(
  //       `${API_URL}/api/calculate/convert-request-data`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Basic ${encodedCredentials}`,
  //           "x-auth-token": token,
  //           "convertion-type": conversionType,
  //         },
  //         body: JSON.stringify(requestBody),
  //       },
  //     );

  //     if (response.ok) {
  //       const result = await response.json();
  //       const convertedData = result.data.requestData;

  //       // Update input fields with converted values
  //       setFormData((prev) => ({
  //         ...prev,
  //         internalDiameter: convertedData.internalDiameter || 0,
  //         lengthLined: convertedData.lengthLined || 0,
  //         holeDiameter: convertedData.holeDiameter || 0,
  //         depthToInvert: convertedData.depthToInvert || 0,
  //         groundwaterDepth: convertedData.groundwaterDepth || 0,
  //         liveLoad: convertedData.liveLoad || 0,
  //         vacuumCondition: convertedData.vacuumCondition,
  //         resin: convertedData.resin,
  //         soilDensity: convertedData.soilDensity || 0,
  //         modulusOfSoilRtn: convertedData.modulusOfSoilRtn || 0,
  //         workingPressure: convertedData.workingPressure || 0,
  //       }));

  //       // setToastList((prev) => [
  //       //   ...prev,
  //       //   {
  //       //     id: Date.now(),
  //       //     title: "SUCCESS!",
  //       //     description: result.message || "Units converted successfully",
  //       //     variant: "default",
  //       //   },
  //       // ]);
  //     } else {
  //       throw new Error("Conversion failed");
  //     }
  //   } catch (error) {
  //     console.error("Error converting units:", error);
  //     // setToastList((prev) => [
  //     //   ...prev,
  //     //   {
  //     //     id: Date.now(),
  //     //     title: "ERROR!",
  //     //     description: "Failed to convert units",
  //     //     variant: "destructive",
  //     //   },
  //     // ]);
  //   }
  // };

  const handleGoBack = () => {
    localStorage.setItem("savedUnitSystem", unitSystem);

    if (userType === "customer") {
      navigate("/projectinfo"); // Navigate to Project Info for customers
    } else if (userType === "applicator") {
      navigate("/projectlist"); // Navigate to Project List for applicators
    }
  };

  // const updateUnits = (system) => {
  //   setFormData((prevFormData) => ({
  //     ...prevFormData,
  //   }));
  // };

  const handlePipeDia = (e) => {
    const { name, value } = e.target;

    // Allow only numbers and one decimal point
    if (!/^\d*\.?\d*$/.test(value)) {
      return; // Ignore invalid input
    }

    let formattedValue = value;

    // If input starts with a decimal point, add "0" in front
    if (value.startsWith(".")) {
      formattedValue = "0" + value;
    }

    const numericValue =
      formattedValue.trim() === "" // Handle empty input
        ? ""
        : formattedValue; // Parse as a number

    setFormData((prev) => ({ ...prev, [name]: numericValue }));

    // Conditional logic for alert and validity
    if (
      (unitSystem === "imperial" && numericValue > 42) ||
      (unitSystem === "si" && numericValue > 1066.8)
    ) {
      setShowAlert(true);
      setIsInputValid(false); // Mark input as invalid
    } else {
      setShowAlert(false);
      setIsInputValid(true); // Mark input as valid
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If it's a dropdown (select field), store the value directly
    if (
      name.includes("Version") ||
      name.includes("Condition") ||
      name.includes("resin")
    ) {
      setFormData((prev) => ({
        ...prev,
        [name]: value, // Store selection directly
      }));
      return;
    }

    // Allow only numbers and one decimal point
    if (!/^\d*\.?\d*$/.test(value)) {
      return; // Ignore invalid input
    }

    let formattedValue = value;

    // If input starts with a decimal point, add "0" in front
    if (value.startsWith(".")) {
      formattedValue = "0" + value;
    }

    // Ensure empty input is stored as an empty string (to avoid NaN)
    const expectedValue = formattedValue.trim() === "" ? "" : formattedValue;

    setFormData((prev) => ({
      ...prev,
      [name]: expectedValue,
    }));
  };

  // console.log(formData);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Add or ensure projectId is in formData before validation
    setFormData((prev) => ({
      ...prev,
    }));

    if (!validateForm()) {
      return;
    }

    const dataToSubmit =
      userType === "applicator" ? { ...formData, projectId } : { ...formData };
    setIsLoading(true);
    // Simulate a delay (e.g., for navigation or API call)
    setTimeout(() => {
      setIsLoading(false);
    }, 30000);

    console.log("Submitting formData:", dataToSubmit);

    // console.log(formData)

    try {
      const token = localStorage.getItem("x-auth-token");
      const response = await fetch(`${API_URL}/api/calculate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          "x-auth-token": token,
          "unit-type": unitSystem === "imperial" ? "imperial" : "si",
        },
        body: JSON.stringify(dataToSubmit), // Explicitly include projectId in the request
      });

      if (response.ok) {
        const result = await response.json();
        // console.log(result);

        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: result.data?.message || result?.error || "!",
            variant: "default",
          },
        ]);

        setCurrentPage("Design Summary");
        navigate("/designsummary", {
          state: {
            projectId,
            results: result,
            ...dataToSubmit,
            internalDiameter: formData.internalDiameter,
            lengthLined: formData.lengthLined,
            vacuumCondition: formData.vacuumCondition,
          },
        });
      } else {
        const errorResponse = await response.json();

        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              errorResponse.data?.message ||
              "Calculation failed. Please try again.",
            variant: "destructive",
          },
        ]);
      }
      setIsLoading(false);
    } catch (error) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
      console.error("Error with fetch:", error);
      setIsLoading(false);
    }
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <main className="tw-space-y-0 md:tw-space-y-0 tw-mt-28 md:tw-mt-44 tw-w-full">
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={index}>
                <BreadcrumbItem>
                  {breadcrumb.isCurrent ? (
                    // Render the current page without a link
                    <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                  ) : (
                    // Render the previous pages without links
                    <BreadcrumbLink>{breadcrumb.label}</BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && (
                  <BreadcrumbSeparator>
                    <SlashIcon />
                  </BreadcrumbSeparator>
                )}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-space-x-10">
        {/* Calculator */}
        <div className="tw-order-2 md:tw-order-1 tw-mt-2 md:tw-mt-0 md:tw-w-4/5">
          <h1 className="tw-text-base md:tw-text-lg tw-mb-8 tw-font-bold">
            SIPP Pressure Liner Design Calculator
          </h1>

          <div className="tw-flex tw-items-center tw-justify-between">
            <div className="">
              {/* Change to Information from backend */}
              {userType === "applicator" && (
                <div className="tw-mb-6">
                  <ul>
                    <li>
                      <span className="tw-font-semibold tw-text-[#00B5ED]">
                        Project Title:
                      </span>{" "}
                      {projectDetails.title}
                    </li>
                    <li>
                      <span className="tw-font-semibold tw-text-[#00B5ED]">
                        Project Location:
                      </span>{" "}
                      {projectDetails.location}
                    </li>
                    <li>
                      <span className="tw-font-semibold tw-text-[#00B5ED]">
                        Client:
                      </span>{" "}
                      {projectDetails.clientName}
                    </li>
                    <li>
                      <span className="tw-font-semibold tw-text-[#00B5ED]">
                        SIPP Installer:
                      </span>{" "}
                      {projectDetails.sippInstaller}
                    </li>
                    <li>
                      <span className="tw-font-semibold tw-text-[#00B5ED]">
                        SIPP Calc Design Date:
                      </span>{" "}
                      {projectDetails.sippCalcDesignDate}
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div>
              <Button variant="default" onClick={handleGoBack}>
                <div className="tw-font-semibold tw-text-sm">Go back</div>
              </Button>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-mb-10 md:tw-mb-20 tw-justify-items-center">
            <div className="tw-flex tw-items-center tw-justify-center tw-max-w-[300px] md:tw-max-w-[600px] tw-mx-auto">
              <img
                src="/sippdiag2.png"
                alt="Sign In Illustration"
                className="tw-max-w-full tw-h-auto tw-object-contain md:tw-ml-20"
              />
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2 tw-my-8">
            <label className="tw-text-sm tw-font-medium">Imperial</label>
            <Switch
              checked={unitSystem === "imperial" ? false : true}
              onCheckedChange={handleSliderChange}
              className="tw-slider"
            />
            <span className="tw-text-sm tw-font-medium">SI</span>
          </div>
          <h2 className="md:tw-text-base tw-text-sm tw-font-bold">
            Design Input Properties
          </h2>
          <div className="">
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <div className="tw-flex tw-flex-col tw-space-y-1 tw-mb-3">
                  <label htmlFor="ASTMVersion" className="">
                    Design Method
                  </label>

                  <div className="tw-relative tw-w-full col-sm-2 col-form-label col-form-label-sm">
                    <select
                      className="input-group select-style col-sm-2 col-form-label col-form-label-sm tw-appearance-none tw-pr-8"
                      id="ASTMVersion"
                      name="ASTMVersion"
                      value={formData.ASTMVersion}
                      onChange={handleChange}
                      required
                    >
                      <option hidden value="">
                        Select Design Method
                      </option>
                      {/* <option value="F1216-07a">ASTM F1216-07a</option> */}
                      <option value="F1216-07b">ASTM F1216-24</option>
                    </select>

                    {/* Custom ChevronDown icon */}
                    <ChevronDown className="tw-absolute tw-top-1/2 tw-right-3 tw-transform -tw-translate-y-1/2 tw-pointer-events-none hover:tw-bg-transparent tw-h-4 tw-w-4 tw-opacity-50 md:tw-mr-2" />
                    {errors.ASTMVersion && (
                      <p className="tw-text-red-500 tw-text-xs">
                        {errors.ASTMVersion}
                      </p>
                    )}
                  </div>
                </div>

                <div className="tw-flex tw-flex-col tw-space-y-1">
                  <label htmlFor="internalDiameter" className="">
                    Pipe to be Lined Internal Diameter, D
                  </label>

                  {showAlert && (
                    <Alert variant="destructive" className="tw-mb-4">
                      <div className="tw-flex tw-items-center tw-space-x-2">
                        <TriangleAlert className="tw-h-3 tw-w-3" />
                        <AlertDescription className="tw-text-xs">
                          Contact SIPP Americas
                        </AlertDescription>
                      </div>
                    </Alert>
                  )}

                  <div className="mb-3 tw-flex">
                    <input
                      type="text"
                      className="input-style no-spin tw-mr-2"
                      id="internalDiameter"
                      name="internalDiameter"
                      step="any"
                      placeholder="0.0"
                      value={formData.internalDiameter}
                      onChange={handlePipeDia}
                      onWheel={(e) => e.target.blur()}
                      onBlur={() =>
                        !formData.internalDiameter && setShowAlert(false)
                      } // Hide alert if no value on blur
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault(); // Prevent increment/decrement
                        }
                      }}
                      inputMode="decimal"
                      required
                    />

                    <span className="unitsbg">
                      {unitSystem === "imperial" ? "in" : "mm"}
                    </span>
                  </div>
                  {errors.internalDiameter && (
                    <p className="tw-text-red-500 tw-text-xs">
                      {errors.internalDiameter}
                    </p>
                  )}
                </div>
                <div className="tw-flex tw-flex-col tw-space-y-1">
                  <label htmlFor="lengthLined" className="">
                    Length to be lined, L
                  </label>
                  <div className="tw-flex mb-3">
                    <input
                      type="text"
                      className="form-control no-spin input-style tw-mr-2"
                      id="lengthLined"
                      name="lengthLined"
                      step="any"
                      placeholder="0.0"
                      value={formData.lengthLined}
                      onWheel={(e) => e.target.blur()}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault(); // Prevent increment/decrement
                        }
                      }}
                      inputMode="decimal"
                      required
                    />
                    <span className="unitsbg">
                      {unitSystem === "imperial" ? "ft" : "m"}
                    </span>
                  </div>
                  {errors.lengthLined && (
                    <p className="tw-text-red-500 tw-text-xs">
                      {errors.lengthLined}
                    </p>
                  )}
                </div>

                <div className="tw-flex tw-flex-col tw-space-y-1">
                  <label htmlFor="holeDiameter" className="">
                    Hole Diameter, d
                  </label>
                  {showHAlert && (
                    <Alert className="tw-border-gray-700 tw-mb-4">
                      <div className="tw-flex tw-items-center tw-text-gray-700 tw-space-x-2">
                        <Lightbulb className="tw-h-3 tw-w-3" />
                        <AlertDescription className="tw-text-xs">
                          It can be in mm (max = 25mm) or in inches (max = 1
                          inch)
                        </AlertDescription>
                      </div>
                    </Alert>
                  )}

                  <div className="tw-flex mb-3">
                    <input
                      type="text"
                      className="form-control no-spin input-style tw-mr-2"
                      id="holeDiameter"
                      name="holeDiameter"
                      step="any"
                      placeholder="0.0"
                      onChange={handleChange}
                      value={formData.holeDiameter}
                      onWheel={(e) => e.target.blur()}
                      onFocus={() => setShowHAlert(true)} // Show alert on focus
                      onBlur={() => setShowHAlert(false)} // Hide alert on blur
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault(); // Prevent increment/decrement
                        }
                      }}
                      inputMode="decimal"
                      required
                    />

                    <span className="unitsbg">
                      {unitSystem === "imperial" ? "in" : "mm"}
                    </span>
                  </div>
                  {errors.holeDiameter && (
                    <p className="tw-text-red-500 tw-text-xs">
                      {errors.holeDiameter}
                    </p>
                  )}
                </div>
                <div className="tw-flex tw-flex-col tw-space-y-1">
                  <label htmlFor="depthToInvert" className="">
                    Depth to Pipe Invert, Di
                  </label>
                  <div className="tw-flex mb-3">
                    <input
                      type="text"
                      className="form-control no-spin input-style tw-mr-2"
                      id="depthToInvert"
                      name="depthToInvert"
                      step="any"
                      placeholder="0.0"
                      value={formData.depthToInvert}
                      onWheel={(e) => e.target.blur()}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault(); // Prevent increment/decrement
                        }
                      }}
                      inputMode="decimal"
                      required
                    />

                    <span className="unitsbg">
                      {unitSystem === "imperial" ? "ft" : "m"}
                    </span>
                  </div>
                  {errors.depthToInvert && (
                    <p className="tw-text-red-500 tw-text-xs">
                      {errors.depthToInvert}
                    </p>
                  )}
                </div>
                <div className="tw-flex tw-flex-col tw-space-y-1">
                  <label htmlFor="groundwaterDepth" className="">
                    Groundwater Table Depth from Ground Surface, Hw
                  </label>
                  <div className="tw-flex mb-3">
                    <input
                      type="text"
                      className="form-control no-spin input-style tw-mr-2"
                      id="groundwaterDepth"
                      name="groundwaterDepth"
                      step="any"
                      placeholder="0.0"
                      value={formData.groundwaterDepth}
                      onWheel={(e) => e.target.blur()}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault(); // Prevent increment/decrement
                        }
                      }}
                      inputMode="decimal"
                      required
                    />

                    <span className="unitsbg">
                      {unitSystem === "imperial" ? "ft" : "m"}
                    </span>
                  </div>
                  {errors.groundwaterDepth && (
                    <p className="tw-text-red-500 tw-text-xs">
                      {errors.groundwaterDepth}
                    </p>
                  )}
                </div>
                <div className="tw-flex tw-flex-col tw-space-y-1">
                  <label htmlFor="liveLoad" className="">
                    Live Load, Ws
                  </label>
                  <div className="tw-flex mb-3">
                    <input
                      type="text"
                      className="form-control no-spin input-style tw-mr-2"
                      id="liveLoad"
                      name="liveLoad"
                      step="any"
                      placeholder="0.0"
                      value={formData.liveLoad}
                      onWheel={(e) => e.target.blur()}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault(); // Prevent increment/decrement
                        }
                      }}
                      inputMode="decimal"
                      required
                    />

                    <span className="unitsbg">
                      {unitSystem === "imperial" ? "psi" : "MPa"}
                    </span>
                  </div>
                  {errors.liveLoad && (
                    <p className="tw-text-red-500 tw-text-xs">
                      {errors.liveLoad}
                    </p>
                  )}
                </div>
                <div className="tw-flex tw-flex-col tw-space-y-1 tw-mb-3">
                  <label htmlFor="vacuumCondition" className="">
                    Vacuum Condition, Pv
                  </label>

                  <div className="tw-relative tw-w-full col-sm-2 col-form-label col-form-label-sm">
                    <select
                      className="input-group select-style col-sm-2 col-form-label col-form-label-sm tw-appearance-none tw-pr-8"
                      id="vacuumCondition"
                      name="vacuumCondition"
                      value={formData.vacuumCondition}
                      onChange={handleChange}
                      required
                    >
                      <option hidden value="">
                        Select Vacuum Condition
                      </option>
                      <option value="No Vacuum">No Vacuum</option>
                      <option value="50% Vacuum">50% Vacuum</option>
                      <option value="80% Vacuum">80% Vacuum</option>
                      <option value="Full Vacuum">Full Vacuum</option>
                    </select>

                    {/* Custom ChevronDown icon */}
                    <ChevronDown className="tw-absolute tw-top-1/2 tw-right-3 tw-transform -tw-translate-y-1/2 tw-pointer-events-none tw-text-gray-500 tw-h-4 tw-w-4 tw-opacity-50 md:tw-mr-2" />
                  </div>
                  {errors.vacuumCondition && (
                    <p className="tw-text-red-500 tw-text-xs">
                      {errors.vacuumCondition}
                    </p>
                  )}
                </div>

                {/* <div className="tw-flex tw-flex-col tw-space-y-1">
                  <label htmlFor="ovality" className="">
                    Ovality, &#916;
                  </label>

                  {showOAlert && (
                    <Alert className="tw-border-gray-700 tw-mb-4">
                      <div className="tw-flex tw-items-center tw-text-gray-700 tw-space-x-2">
                        <Lightbulb className="tw-h-3 tw-w-3" />
                        <AlertDescription className="tw-text-xs">
                          Maximum is 2%
                        </AlertDescription>
                      </div>
                    </Alert>
                  )}
                  <div className="tw-flex mb-3">
                    <input
                      type="number"
                      className="form-control no-spin input-style tw-mr-2"
                      id="ovality"
                      name="ovality"
                      step="any"
                      placeholder="0.0"
                      value={formData.ovality}
                      onChange={handleChange}
                      onFocus={() => setShowOAlert(true)} // Show alert on focus
                      onBlur={() => setShowOAlert(false)} // Hide alert on blur
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault(); // Prevent increment/decrement
                        }
                      }}
                      required
                    />

                    <span className="unitsbg">%</span>
                  </div>
                  {errors.ovality && (
                    <p className="tw-text-red-500 tw-text-xs">
                      {errors.ovality}
                    </p>
                  )}
                </div> */}

                {/* <div className="tw-flex tw-flex-col tw-space-y-1">
                  <label htmlFor="soilDensity" className="">
                    Soil density, w
                  </label>
                  <div className="tw-flex mb-3">
                    <input
                      type="number"
                      className="form-control input-style tw-mr-2"
                      id="soilDensity"
                      name="soilDensity"
                      step="any"
                      placeholder="0.0"
                      value={formData.soilDensity}
                      onChange={handleChange}
                      required
                    />

                    <span className="unitsbg">
                      {unitSystem === "Imperial" ? "lb/ft3" : "KN/m3"}
                    </span>
                  </div>
                </div>
                <div className="tw-flex tw-flex-col tw-space-y-1">
                  <label htmlFor="modulusOfSoilRtn" className="">
                    Modulus of soil rtn, E's
                  </label>
                  <div className="tw-flex mb-3">
                    <input
                      type="number"
                      className="form-control input-style tw-mr-2"
                      id="modulusOfSoilRtn"
                      name="modulusOfSoilRtn"
                      step="any"
                      placeholder="0.0"
                      value={formData.modulusOfSoilRtn}
                      onChange={handleChange}
                      required
                    />

                    <span className="unitsbg">
                      {unitSystem === "Imperial" ? "psi" : "MPa"}
                    </span>
                  </div>
                </div> */}
              </div>

              {isLoading ? (
                <LoadingButton />
              ) : (
                <Button
                  type="submit"
                  disabled={!isInputValid || !isFormComplete}
                  className="tw-w-full tw-font-semibold tw-my-6 md:tw-my-8 tw-text-sm"
                >
                  CALCULATE
                </Button>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </main>
  );
};

export default Calculator;
